/**
 * KOS Cart Drawer Web Component
 * File: assets/component-cart-drawer.js
 */

(function() {
  if (customElements.get('cart-drawer')) return;

  /* --------------------------------------------------------------------------
     Cart Drawer Web Component
     -------------------------------------------------------------------------- */
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.focusableElements = [];
      this.previousActiveElement = null;
      this.shippingThreshold = parseInt(this.dataset.shippingThreshold) || 7500;
    }

    connectedCallback() {
      console.log('CartDrawer connected');
      this.attachEvents();
      this.setupGlobalListeners();
    }

    attachEvents() {
      this.querySelectorAll('[data-drawer-close]').forEach(el => {
        el.addEventListener('click', () => this.close());
      });
      this.addEventListener('click', this.handleClick.bind(this));
      document.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    setupGlobalListeners() {
      // Quick add buttons (collection pages, etc)
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-add-to-cart]');
        if (!btn) return;
        
        e.preventDefault();
        const variantId = btn.dataset.productId || btn.dataset.variantId;
        if (!variantId) return;
        
        this.addToCart({
          variantId: parseInt(variantId),
          quantity: 1,
          button: btn
        });
      });

      // PDP form submissions
      document.addEventListener('cart:request-add', (e) => {
        console.log('Cart drawer received cart:request-add', e.detail);
        this.addToCart(e.detail);
      });

      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:refresh', () => this.refresh());
    }

    async addToCart({ variantId, quantity = 1, sellingPlan = null, button = null }) {
      console.log('addToCart called', { variantId, quantity, sellingPlan });
      
      if (!variantId) {
        console.error('No variantId provided');
        return;
      }

      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
      }

      const body = {
        id: parseInt(variantId),
        quantity: quantity
      };

      if (sellingPlan) {
        body.selling_plan = parseInt(sellingPlan);
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.description || 'Failed to add to cart');
        }

        await this.refresh();
        
        // Show success state
        if (button) {
          button.classList.remove('is-loading');
          button.classList.add('is-added');
          
          const loaderText = button.querySelector('.pdp-add-btn__loader-text');
          if (loaderText) loaderText.textContent = 'Added!';
          
          setTimeout(() => {
            button.classList.remove('is-added');
            if (loaderText) loaderText.textContent = 'Adding...';
            button.disabled = false;
          }, 1500);
        }
        
        this.open();
        this.announce('Item added to cart');

      } catch (err) {
        console.error('Add to cart error:', err);
        this.announce('Error adding to cart');
        
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      }
    }

    handleClick(e) {
      const target = e.target;
      
      if (target.closest('[data-qty="decrease"]')) {
        const btn = target.closest('[data-qty="decrease"]');
        if (!btn.disabled) {
          this.updateQuantity(btn.dataset.line, -1);
        }
        return;
      }
      
      if (target.closest('[data-qty="increase"]')) {
        const btn = target.closest('[data-qty="increase"]');
        this.updateQuantity(btn.dataset.line, 1);
        return;
      }
      
      if (target.closest('[data-remove]')) {
        const btn = target.closest('[data-remove]');
        this.removeItem(btn.dataset.line);
        return;
      }
      
      if (target.closest('[data-toggle-subscription]')) {
        const btn = target.closest('[data-toggle-subscription]');
        this.toggleSubscription(btn);
        return;
      }
    }

    handleKeydown(e) {
      if (!this.isOpen) return;
      
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      
      if (e.key === 'Tab') {
        this.trapFocus(e);
      }
    }

    trapFocus(e) {
      const focusable = this.getFocusableElements();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }

    getFocusableElements() {
      const selectors = 'button:not([disabled]):not([aria-disabled="true"]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return [...this.querySelectorAll(selectors)].filter(el => el.offsetParent !== null);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      if (this.isOpen) return;
      
      this.previousActiveElement = document.activeElement;
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      
      requestAnimationFrame(() => {
        const closeBtn = this.querySelector('.cart-drawer__close');
        closeBtn?.focus();
      });
      
      this.announce('Cart opened');
    }

    close() {
      if (!this.isOpen) return;
      
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      this.previousActiveElement?.focus();
      
      this.announce('Cart closed');
    }

    announce(message) {
      const announcer = this.querySelector('[data-cart-announcer]');
      if (announcer) {
        announcer.textContent = message;
        setTimeout(() => { announcer.textContent = ''; }, 1000);
      }
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
    }

    async updateQuantity(line, delta) {
      const item = this.querySelector(`[data-line="${line}"]`);
      if (!item) return;
      
      const qtyEl = item.querySelector('[data-qty-value]');
      const currentQty = parseInt(qtyEl.textContent);
      const newQty = Math.max(0, currentQty + delta);
      
      if (newQty === 0) {
        return this.removeItem(line);
      }
      
      this.setLoading(true);
      
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line), quantity: newQty })
        });

        if (!res.ok) throw new Error('Failed');

        // /cart/change.js sometimes returns the cart before discount allocations
        // have been re-evaluated, so per-line allocations come back empty.
        // Fetch /cart.js for the fully-resolved state so discount tags re-render.
        await this.refresh();
        this.announce(`Quantity updated to ${newQty}`);
      } catch (err) {
        console.error('Cart update error:', err);
        this.announce('Error updating cart');
      } finally {
        this.setLoading(false);
      }
    }

    async removeItem(line) {
      const item = this.querySelector(`[data-line="${line}"]`);
      const name = item?.querySelector('.cart-item__name')?.textContent || 'Item';
      
      this.setLoading(true);
      
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line), quantity: 0 })
        });

        if (!res.ok) throw new Error('Failed');

        // Same reason as updateQuantity — fetch fresh /cart.js so cart-level
        // discount allocations are re-allocated across remaining lines.
        await this.refresh();
        this.announce(`${name} removed from cart`);
      } catch (err) {
        console.error('Cart remove error:', err);
        this.announce('Error removing item');
      } finally {
        this.setLoading(false);
      }
    }

    async toggleSubscription(btn) {
      const line = btn.dataset.line;
      const variantId = parseInt(btn.dataset.variantId);
      const isSubscription = btn.dataset.isSubscription === 'true';
      const productHandle = btn.dataset.productHandle;
      
      this.setLoading(true);
      
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line), quantity: 0 })
        });
        
        if (isSubscription) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
        } else {
          const productRes = await fetch(`/products/${productHandle}.js`);
          const product = await productRes.json();
          const plan = this.firstPublicPlan(product);
          
          if (!plan) throw new Error('No subscription');
          
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              selling_plan: plan.id
            })
          });
        }
        
        await this.refresh();
        this.announce(isSubscription ? 'Switched to one-time' : 'Subscription added');
      } catch (err) {
        console.error('Subscription error:', err);
        this.announce('Error updating subscription');
      } finally {
        this.setLoading(false);
      }
    }

    async refresh() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        await this.renderCart(cart);
      } catch (err) {
        console.error('Refresh error:', err);
      }
    }

    renderDiscounts(cart) {
      const container = this.querySelector('[data-cart-discounts]');
      if (!container) return;

      // /cart.js may return cart_level_discount_applications, but some shops/
      // discount-app combos surface the data via discount_applications or
      // discounts instead — try each in order.
      let discounts =
        cart.cart_level_discount_applications
        || (cart.discount_applications || []).filter((d) => d.target_type !== 'shipping_line')
        || cart.discounts
        || [];

      // If no cart-level discounts but cart.total_discount > 0, synthesize one
      // row from the totals so the customer still sees their savings.
      if (discounts.length === 0 && cart.total_discount > 0) {
        discounts = [{ title: 'Discount', total_allocated_amount: cart.total_discount }];
      }

      if (discounts.length === 0) {
        container.innerHTML = '';
        return;
      }

      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));

      container.innerHTML = discounts.map((d) => {
        // Field name varies: total_allocated_amount (modern) / amount (legacy)
        const rawAmount = d.total_allocated_amount ?? d.amount ?? 0;
        const amount = Number(rawAmount);
        const safeAmount = Number.isFinite(amount) ? amount : 0;
        const title = d.title || d.code || 'Discount';

        return `
          <div class="cart-summary__row cart-summary__row--discount">
            <span class="cart-summary__label cart-summary__label--discount">
              <svg class="cart-summary__discount-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                <line x1="7" y1="7" x2="7.01" y2="7"/>
              </svg>
              <span class="cart-summary__discount-title">${esc(title)}</span>
            </span>
            <span class="cart-summary__value cart-summary__value--discount">
              −${this.formatMoney(safeAmount)}
            </span>
          </div>
        `;
      }).join('');
    }

    /* Shopify /cart.js returns cart-level discounts in cart_level_discount_applications
       but does NOT include them in item.line_level_discount_allocations. To show per-item
       VIP30-style tags after a refresh, we split each cart-level discount across the
       items proportionally to their line price (the "across" allocation method that
       Shopify uses by default for cart-wide discounts). */
    computeCartLevelLineAllocations(cart) {
      const out = {};
      const discounts = (cart.cart_level_discount_applications || [])
        .filter((d) => d.target_type !== 'shipping_line' && d.total_allocated_amount > 0);
      if (!discounts.length || !cart.items?.length) return out;

      const totalLinePrice = cart.items.reduce((s, i) => s + (i.original_line_price || 0), 0);
      if (totalLinePrice <= 0) return out;

      discounts.forEach((d) => {
        cart.items.forEach((item) => {
          const share = item.original_line_price / totalLinePrice;
          const allocated = Math.round(d.total_allocated_amount * share);
          if (allocated <= 0) return;
          if (!out[item.key]) out[item.key] = [];
          out[item.key].push({
            amount: allocated,
            discount_application: { title: d.title, type: d.type },
          });
        });
      });

      return out;
    }

    async renderCart(cart) {
      const itemsContainer = this.querySelector('[data-cart-items]');
      const emptyState = this.querySelector('[data-cart-empty]');
      const footer = this.querySelector('[data-cart-footer]');
      const isEmpty = cart.item_count === 0;

      // Pre-compute cart-level allocations once per render so renderItem can use them.
      this._cartLevelAllocations = this.computeCartLevelLineAllocations(cart);

      this.querySelector('[data-cart-count]').textContent = cart.item_count;
      this.querySelector('[data-cart-subtotal]').textContent = this.formatMoney(cart.items_subtotal_price);
      this.querySelector('[data-cart-total]').textContent = this.formatMoney(cart.total_price);

      this.renderDiscounts(cart);
      this.updateShippingProgress(cart.total_price);

      itemsContainer?.toggleAttribute('hidden', isEmpty);
      emptyState?.toggleAttribute('hidden', !isEmpty);
      footer?.toggleAttribute('hidden', isEmpty);

      const checkoutBtn = this.querySelector('[data-checkout-btn]');
      if (checkoutBtn) {
        if (isEmpty) {
          checkoutBtn.setAttribute('aria-disabled', 'true');
          checkoutBtn.setAttribute('tabindex', '-1');
        } else {
          checkoutBtn.removeAttribute('aria-disabled');
          checkoutBtn.removeAttribute('tabindex');
        }
      }

      this.updateHeaderCount(cart.item_count);

      if (!isEmpty && itemsContainer) {
        await this.fetchSellingPlanData(cart.items);
        itemsContainer.innerHTML = cart.items.map((item, index) => this.renderItem(item, index + 1)).join('');
      }
    }

    /* First Skio plan that isn't reserved for a landing-page offer (see Theme.isLandingOnlyPlan). */
    firstPublicPlan(product) {
      const reserved = (group, plan) => typeof Theme !== 'undefined' && Theme.isLandingOnlyPlan ? Theme.isLandingOnlyPlan(group, plan) : false;
      for (const group of (product && product.selling_plan_groups) || []) {
        if (group.app_id !== 'SKIO') continue;
        const plan = (group.selling_plans || []).find(p => !reserved(group, p));
        if (plan) return plan;
      }
      return null;
    }

    async fetchSellingPlanData(items) {
      if (!this._sellingPlanCache) this._sellingPlanCache = {};

      // Get unique handles not yet cached
      const handles = [...new Set(items.map(i => i.handle))].filter(h => !(h in this._sellingPlanCache));

      await Promise.all(handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) throw new Error();
          const product = await res.json();
          const firstPlan = this.firstPublicPlan(product);

          if (firstPlan) {
            let discount = 0;
            if (firstPlan.price_adjustments && firstPlan.price_adjustments.length > 0) {
              const adj = firstPlan.price_adjustments[0];
              if (adj.value_type === 'percentage') discount = adj.value;
            }
            this._sellingPlanCache[handle] = { hasSubscription: true, discount };
          } else {
            this._sellingPlanCache[handle] = { hasSubscription: false, discount: 0 };
          }
        } catch {
          this._sellingPlanCache[handle] = { hasSubscription: false, discount: 0 };
        }
      }));
    }

    renderItem(item, line) {
      const isSubscription = !!item.selling_plan_allocation;
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

      // Merge line-level allocations (from item) with cart-level allocations
      // (computed in renderCart from cart_level_discount_applications) so
      // coupons like VIP30 show as per-item tags after a qty refresh.
      const lineLevel = item.line_level_discount_allocations || [];
      const cartLevel = (this._cartLevelAllocations && this._cartLevelAllocations[item.key]) || [];
      const allocations = [...lineLevel, ...cartLevel];

      const lineDiscount = allocations.reduce(
        (sum, a) => sum + (Number(a.amount) || 0),
        0
      );
      const effectiveLinePrice = item.original_line_price - lineDiscount;
      const hasDiscount = lineDiscount > 0;

      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));

      const discountsHTML = hasDiscount
        ? `<div class="cart-item__discounts">${
            allocations.map((a) => {
              const title = a.discount_application?.title || a.discount_application?.code || 'Discount';
              const amount = Number(a.amount) || 0;
              return `
                <span class="cart-item__discount-tag">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                    <line x1="7" y1="7" x2="7.01" y2="7"/>
                  </svg>
                  <span class="cart-item__discount-name">${esc(title)}</span>
                  <span class="cart-item__discount-amount">−${this.formatMoney(amount)}</span>
                </span>
              `;
            }).join('')
          }</div>`
        : '';

      // Check if product has selling plans cached from fetchSellingPlanData
      const planData = this._sellingPlanCache?.[item.handle];
      const hasSubscriptionOption = planData?.hasSubscription || false;
      const subscriptionDiscount = planData?.discount || 0;

      let metaHTML = '';
      if (isSubscription) {
        metaHTML = `
          <span class="cart-item__subscription">
            <span>↻</span> ${item.selling_plan_allocation.selling_plan.name}
          </span>
          <button type="button" class="cart-item__toggle" data-toggle-subscription data-line="${line}" data-variant-id="${item.variant_id}" data-is-subscription="true">
            Switch to one-time
          </button>
        `;
      } else if (hasSubscriptionOption) {
        metaHTML = `
          <button type="button" class="cart-item__toggle cart-item__toggle--subscribe" data-toggle-subscription data-line="${line}" data-variant-id="${item.variant_id}" data-product-handle="${item.handle}" data-is-subscription="false">
            Subscribe & save ${subscriptionDiscount > 0 ? `<span class="cart-item__toggle-savings">${subscriptionDiscount}%</span>` : ''}
          </button>
        `;
      }

      return `
        <article class="cart-item" data-line="${line}" data-variant-id="${item.variant_id}" data-key="${item.key}">
          <div class="cart-item__image">
            ${item.image ? `<img src="${this.getSizedImage(item.image, '200x')}" alt="" width="72" height="72" loading="lazy">` : ''}
          </div>

          <div class="cart-item__details">
            <div class="cart-item__top">
              <div class="cart-item__info">
                <a href="${item.url}" class="cart-item__name">${item.product_title}</a>
                ${variantTitle ? `<span class="cart-item__variant">${variantTitle}</span>` : ''}
              </div>
              <div class="cart-item__pricing">
                <span class="cart-item__price${hasDiscount ? ' cart-item__price--sale' : ''}">${this.formatMoney(effectiveLinePrice)}</span>
                ${hasDiscount ? `<span class="cart-item__price-original">${this.formatMoney(item.original_line_price)}</span>` : ''}
              </div>
            </div>

            ${discountsHTML}

            ${metaHTML ? `<div class="cart-item__meta">${metaHTML}</div>` : ''}

            <div class="cart-item__footer">
              <div class="cart-item__quantity">
                <button type="button" class="cart-item__qty-btn" data-qty="decrease" data-line="${line}" aria-label="Decrease quantity" ${item.quantity <= 1 ? 'disabled' : ''}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M2 6h8"/></svg>
                </button>
                <span class="cart-item__qty-value" data-qty-value>${item.quantity}</span>
                <button type="button" class="cart-item__qty-btn" data-qty="increase" data-line="${line}" aria-label="Increase quantity">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 2v8M2 6h8"/></svg>
                </button>
              </div>
              <button type="button" class="cart-item__remove" data-remove data-line="${line}" aria-label="Remove ${item.product_title}">Remove</button>
            </div>
          </div>
        </article>
      `;
    }

    updateShippingProgress(totalCents) {
      const remaining = Math.max(0, this.shippingThreshold - totalCents);
      const progress = Math.min(100, (totalCents / this.shippingThreshold) * 100);
      const isFree = totalCents >= this.shippingThreshold;
      
      const section = this.querySelector('[data-shipping-section]');
      const fill = this.querySelector('[data-shipping-fill]');
      const message = this.querySelector('[data-shipping-message]');
      const shippingValue = this.querySelector('[data-shipping-value]');
      
      section?.classList.toggle('cart-shipping--unlocked', isFree);
      
      if (fill) fill.style.width = `${progress}%`;
      
      if (message) {
        message.innerHTML = isFree
          ? `You've unlocked <strong>FREE shipping!</strong>`
          : `You're <strong>${this.formatMoney(remaining)}</strong> away from free shipping`;
      }
      
      if (shippingValue) {
        shippingValue.textContent = isFree ? 'FREE' : 'Calculated at checkout';
        shippingValue.classList.toggle('cart-summary__value--free', isFree);
      }
    }

    updateHeaderCount(count) {
      document.querySelectorAll('[data-header-cart-count]').forEach(el => {
        el.textContent = count;
        el.classList.toggle('is-hidden', count === 0);
      });
      
      const cartBtn = document.querySelector('[data-cart-toggle]');
      if (cartBtn) {
        cartBtn.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
      }
    }

    formatMoney(cents) {
      const num = Number(cents);
      const safe = Number.isFinite(num) ? num : 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(safe / 100);
    }

    getSizedImage(src, size) {
      if (!src) return '';
      return src.replace(/(\.[^.]+)$/, `_${size}$1`).replace('http:', 'https:');
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();